/**
 * W-7 · Départs vers l'aéroport.
 *
 * Refonte du 2026-10-14 (retour du porteur : « on se perd rapidement ») — la
 * page suit maintenant UNE règle : un écran, une action. Elle montre l'état du
 * fichier en cours et le prochain geste, rien d'autre au premier regard :
 *
 *   déposer → (reconnaître les hôtels inconnus) → publier → en ligne.
 *
 * Tout le reste — ce qui est en ligne, les fichiers précédents, la liste des
 * hôtels, le détail de la lecture — est replié derrière un titre qu'on ouvre
 * quand on en a besoin. Le vocabulaire est celui du TROC, pas du développeur :
 * « À publier », « En ligne », « Ancien », « C'est quel hôtel ? ».
 *
 * Les colonnes organisation et effectif sont visibles ici, entre opérateurs ;
 * elles ne quittent jamais le back-office (voir PRIVACY.md § Départs).
 */

import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type {
  DepartureFileDetail,
  DepartureFileSummary,
  DepartureHotel,
  DeparturePublishResult,
  DepartureUnknownHotel,
} from '../lib/api';
import { useApi } from '../lib/useApi';
import { toast } from '../components/Toast';
import { formatDateTime, formatInt, formatLongDay, formatSince } from '../lib/format';
import { Async, Badge, Banner, Card, Loading } from '../components/ui';
import type { Tone } from '../components/ui';
import { READ_ONLY_NOTICE, useCan } from '../lib/session';

const STATUS_LABELS: Record<string, string> = {
  draft: 'À publier',
  published: 'En ligne',
  superseded: 'Ancien',
  rejected: 'Refusé',
};
function statusTone(status: string): Tone {
  return status === 'published' ? 'ok' : status === 'rejected' ? 'bad' : status === 'draft' ? 'warn' : 'neutral';
}

const FIELD_LABELS: Record<string, string> = {
  assignment: 'Mode de transport',
  busNumber: 'Numéro de bus',
  boardingPlace: "Lieu d'embarquement",
  boardingTime: "Heure d'embarquement",
  terTime: 'Heure du train (TER)',
  organization: 'Organisation',
  personCount: 'Nombre de personnes',
  hotel: 'Hébergement',
  flightDate: 'Date du vol',
  flightTime: 'Heure du vol',
  flightNumber: 'Numéro de vol',
  arrivalTime: "Arrivée à l'aéroport",
  station: 'Gare',
  logService: 'Service logistique',
  driverPhone: 'Téléphone du chauffeur',
};

/** Journée « jeu. 12 novembre 2026 » depuis 'YYYY-MM-DD', sans passer par un fuseau. */
function dayLabel(date: string): string {
  return formatLongDay(`${date}T12:00:00Z`);
}
const DAY_FORMAT = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
/** « le jeudi 12 novembre », « les jeudi 12 et vendredi 13 novembre » : une phrase, pas une liste. */
function daysPhrase(dates: string[]): string {
  const names = dates.map((d) => DAY_FORMAT.format(new Date(`${d}T12:00:00Z`)));
  if (names.length === 0) return '';
  if (names.length === 1) return `le ${names[0]}`;
  return `les ${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}`;
}

/** « Dimanche 1er novembre » : le titre d'une journée dans le détail de la lecture. */
function dayHeading(date: string): string {
  const name = DAY_FORMAT.format(new Date(`${date}T12:00:00Z`)).replace(/ 1 /, ' 1er ');
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** Le vol n'a pas lieu le jour de l'embarquement : on le dit en mots, sans répéter une date. */
function flightDayNote(boardingDate: string, flightDate: string): string {
  if (flightDate === boardingDate) return '';
  const next = new Date(`${boardingDate}T12:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  if (next.toISOString().slice(0, 10) === flightDate) return 'vol le lendemain';
  return `vol le ${DAY_FORMAT.format(new Date(`${flightDate}T12:00:00Z`))}`;
}

/** Les transferts regroupés par journée d'embarquement, chaque journée triée par heure. */
function groupByDay<T extends { boardingDate: string; boardingTime: string }>(rows: T[]): Array<{ date: string; rows: T[] }> {
  const byDate = new Map<string, T[]>();
  for (const row of rows) {
    const list = byDate.get(row.boardingDate) ?? [];
    list.push(row);
    byDate.set(row.boardingDate, list);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, list]) => ({ date, rows: [...list].sort((a, b) => a.boardingTime.localeCompare(b.boardingTime)) }));
}

/**
 * « Bus direct 11 », pas « Bus direct Direct Bus 11 » : l'équipe transport écrit
 * souvent le mode dans la colonne du numéro (« Direct Bus 11 », « Shuttle »).
 */
function busLabel(kind: 'Bus direct' | 'Navette', busNumber: string | null): string {
  const n = busNumber?.trim() ?? '';
  if (n === '') return kind;
  if (/^direct\s*bus\b/i.test(n)) return `Bus direct${n.replace(/^direct\s*bus\b/i, '')}`.trim();
  if (/^(shuttle|navette)\b/i.test(n)) return `Navette${n.replace(/^(shuttle|navette)\b/i, '')}`.trim();
  if (/^bus\b/i.test(n)) return `${kind}${n.replace(/^bus\b/i, '')}`.trim();
  return `${kind} ${n}`;
}

function modeLabel(row: { mode: string; busNumber: string | null; lineNos: string[]; terTime: string | null }): string {
  if (row.mode === 'direct_bus') return busLabel('Bus direct', row.busNumber);
  if (row.mode === 'shuttle_ter') return `${busLabel('Navette', row.busNumber)}${row.terTime ? ` + TER ${row.terTime}` : ' + TER'}`;
  const lines = row.lineNos.length > 0 ? row.lineNos.join(' ou ') : 'Ligne';
  return `${lines}${row.terTime ? ` + TER ${row.terTime}` : ' + TER'}`;
}

/** Un seul libellé pour l'issue d'un envoi vers l'application, partout. */
function pushLabel(ok: boolean, skipped: boolean | undefined, error: string | undefined): string {
  if (ok) return '';
  if (skipped) return "L'envoi vers l'application n'est pas configuré sur ce serveur : les accrédités voient encore l'ancien plan.";
  return `${error ?? 'service injoignable'}. Les accrédités voient encore l'ancien plan.`;
}

export function DeparturesPage(): React.ReactElement {
  const canPublish = useCan('departures:publish');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [lastPublish, setLastPublish] = useState<DeparturePublishResult | null>(null);
  const [nonce, setNonce] = useState(0);
  // Fichiers retirés pendant cette visite : la liste rechargée ne les contient
  // plus, mais entre le retrait et son rechargement elle les porte encore - et
  // le rappel ci-dessous les rouvrirait (vu le 2026-10-14 : 404 puis fichier fantôme).
  const removedIds = useRef(new Set<string>());

  const current = useApi((s) => api.departuresCurrent(s), `departures-current|${nonce}`);
  const files = useApi((s) => api.departureFiles(50, s), `departures-files|${nonce}`);
  const hotels = useApi((s) => api.departureHotels(s), `departures-hotels|${nonce}`);
  const detail = useApi((s) => (selectedId ? api.departureFile(selectedId, s) : Promise.resolve(null)), `departures-file|${selectedId ?? ''}|${nonce}`, {
    enabled: selectedId !== null,
  });

  // On reprend là où on en était : un fichier déposé mais pas encore publié
  // s'affiche tout seul à l'ouverture de la page. C'est le cas de l'opérateur
  // qui a déposé le fichier, fermé l'onglet, et revient pour publier.
  useEffect(() => {
    if (selectedId !== null || !files.data) return;
    const pending = files.data.find((f) => f.status === 'draft' && !removedIds.current.has(f.id));
    if (pending) setSelectedId(pending.id);
  }, [files.data, selectedId]);

  const refresh = (): void => setNonce((n) => n + 1);

  const run = async (key: string, action: () => Promise<void>): Promise<void> => {
    setBusy(key);
    setActionError(null);
    try {
      await action();
    } catch (cause) {
      setActionError(cause instanceof ApiError ? cause : new ApiError(0, { title: String(cause) }, true));
    } finally {
      setBusy(null);
    }
  };

  const onUpload = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    await run('upload', async () => {
      const out = await api.uploadDepartureFile(file);
      setLastPublish(null);
      setSelectedId(out.file.id);
      refresh();
    });
  };

  return (
    <div className="stack">
      {canPublish ? null : (
        <Banner tone="info" title="Lecture seule">
          {READ_ONLY_NOTICE} Déposer et publier un plan demande un opérateur TROC ou un administrateur.
        </Banner>
      )}

      {actionError ? (
        <Banner tone="bad" title="Ça n'a pas marché">
          {actionError.userMessage}
          {actionError.problem.detail ? ` : ${actionError.problem.detail}` : ''}
        </Banner>
      ) : null}

      {lastPublish && !lastPublish.push.ok ? (
        <Banner tone="warn" title="Publié ici, mais l'application n'a pas pu être mise à jour">
          {pushLabel(false, lastPublish.push.skipped, lastPublish.push.error)}{' '}
          <button aria-busy={busy === 'push'}
            type="button"
            className="btn btn-sm"
            disabled={busy !== null || !canPublish}
            onClick={() =>
              void run('push', async () => {
                const out = await api.pushDepartureFile(lastPublish.fileId);
                setLastPublish({ ...lastPublish, push: out.push });
                refresh();
              })
            }
          >
            Réessayer l'envoi
          </button>
        </Banner>
      ) : null}

      {/* ── L'écran principal : le fichier en cours, ou la zone de dépôt ── */}
      {selectedId ? (
        <Async state={detail}>
          {(d) =>
            // `useApi` garde la réponse précédente pendant un chargement : sans
            // ce test, le fichier d'avant s'afficherait sous le nom du suivant.
            d && d.file.id === selectedId ? (
              <FileDetail
                detail={d}
                hotels={hotels.data ?? []}
                canPublish={canPublish}
                busy={busy}
                justPublished={lastPublish?.fileId === d.file.id && lastPublish.push.ok}
                onUpload={onUpload}
                onClose={() => {
                  setSelectedId(null);
                  setLastPublish(null);
                }}
                onAlias={(hotelId, label) =>
                  run(`alias:${label}`, async () => {
                    await api.addDepartureHotelAlias(hotelId, label);
                    refresh();
                  })
                }
                onCreate={(unknown) =>
                  run(`create:${unknown.key}`, async () => {
                    await api.createDepartureHotel({ name: unknown.label, aliases: [unknown.label] });
                    refresh();
                  })
                }
                onSynonym={(field, header) =>
                  run(`syn:${header}`, async () => {
                    await api.addDepartureColumnSynonym(field, header);
                    refresh();
                  })
                }
                onPublish={() =>
                  run('publish', async () => {
                    const out = await api.publishDepartureFile(d.file.id);
                    setLastPublish(out);
                    refresh();
                  })
                }
                onRepublish={() =>
                  run('republish', async () => {
                    const out = await api.republishDepartureFile(d.file.id);
                    setLastPublish(out);
                    refresh();
                  })
                }
                onUnpublish={() =>
                  run('unpublish', async () => {
                    const out = await api.unpublishDepartureFile(d.file.id);
                    setLastPublish(null);
                    if (out.push.ok) {
                      toast(
                        out.remainingDates.length > 0
                          ? `Retiré. L'application sert encore ${daysPhrase(out.remainingDates)}.`
                          : "Retiré. Plus rien n'est publié : les accrédités voient « pas encore disponible ».",
                      );
                    } else {
                      toast("Retiré ici. L'envoi à l'application est désactivé sur ce serveur.");
                    }
                    refresh();
                  })
                }
                onDelete={() =>
                  run('delete', async () => {
                    await api.deleteDepartureFile(d.file.id);
                    removedIds.current.add(d.file.id);
                    toast("Fichier retiré. Rien n'a changé pour les accrédités.");
                    setSelectedId(null);
                    setLastPublish(null);
                    refresh();
                  })
                }
              />
            ) : (
              <Loading label="Ouverture du fichier…" />
            )
          }
        </Async>
      ) : (
        <DropCard busy={busy} canPublish={canPublish} onUpload={onUpload} />
      )}

      {/* ── Ce qui est en ligne : une ligne par journée, lisible d'un coup d'œil ── */}
      <Card title="En ligne pour les accrédités">
        <Async state={current}>
          {(cur) => {
            const dates = Object.keys(cur.byDate).sort();
            if (dates.length === 0) {
              return (
                <p className="muted">
                  Rien n'est publié pour l'instant. Dans l'application, les accrédités voient « pas encore disponible ».
                </p>
              );
            }
            return (
              <dl className="dep-kv">
                {dates.map((date) => {
                  const file = cur.byDate[date];
                  if (!file) return null;
                  return (
                    <div key={date} className="dep-kv-row">
                      <dt>{dayLabel(date)}</dt>
                      <dd>
                        {formatInt(file.dayRowCount)} transferts · publié {file.publishedAt ? formatSince(file.publishedAt) : ''} par{' '}
                        {file.publishedBy?.displayName ?? 'inconnu'} ·{' '}
                        {file.pushedAt ? <Badge tone="ok">dans l'application</Badge> : <Badge tone="warn">pas encore dans l'application</Badge>}{' '}
                        <button type="button" className="dep-link small" onClick={() => setSelectedId(file.id)}>
                          voir le fichier
                        </button>
                      </dd>
                    </div>
                  );
                })}
                {cur.pushConfigured ? null : (
                  <div className="dep-kv-row">
                    <dt>Envoi</dt>
                    <dd className="muted">L'envoi vers l'application n'est pas configuré sur ce serveur.</dd>
                  </div>
                )}
              </dl>
            );
          }}
        </Async>
      </Card>

      {/* ── Le reste, replié ── */}
      <details className="fold">
        <summary>Fichiers précédents</summary>
        <Async state={files}>
          {(items) =>
            items.length === 0 ? (
              <p className="muted">Aucun fichier reçu pour l'instant.</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Fichier</th>
                      <th>Reçu</th>
                      <th>Journées</th>
                      <th>État</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((f) => (
                      <FileRow key={f.id} file={f} selected={f.id === selectedId} onOpen={() => setSelectedId(f.id)} />
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Async>
      </details>

      <details className="fold">
        <summary>Liste des hôtels connus de l'application</summary>
        <HotelsCard hotels={hotels} canPublish={canPublish} busy={busy} onRefresh={refresh} run={run} />
      </details>
    </div>
  );
}

// ─── Dépôt ──────────────────────────────────────────────────────────────────

function DropCard(props: { busy: string | null; canPublish: boolean; onUpload: (file: File | undefined) => Promise<void> }): React.ReactElement {
  const { busy, canPublish, onUpload } = props;
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [over, setOver] = useState(false);
  const disabled = !canPublish || busy !== null;

  return (
    <Card>
      <div
        className={`dep-drop${over ? ' is-over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          if (!disabled) void onUpload(e.dataTransfer.files?.[0]);
        }}
      >
        <strong>Déposez ici le fichier Excel de l'équipe transport</strong>
        <p>Il est lu tout de suite. Rien n'est publié tant que vous n'avez pas cliqué « Publier ».</p>
        <input
          ref={fileInput}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="sr-only"
          id="dep-file"
          disabled={disabled}
          onChange={(e) => {
            void onUpload(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
        <button type="button" className="btn btn-primary" disabled={disabled} title={canPublish ? undefined : READ_ONLY_NOTICE} onClick={() => fileInput.current?.click()}>
          {busy === 'upload' ? 'Lecture du fichier…' : 'Choisir le fichier sur mon poste'}
        </button>
      </div>
    </Card>
  );
}

// ─── Historique ─────────────────────────────────────────────────────────────

function FileRow(props: { file: DepartureFileSummary; selected: boolean; onOpen: () => void }): React.ReactElement {
  const { file, selected, onOpen } = props;
  return (
    <tr className={selected ? 'is-selected' : undefined}>
      <td>
        <button type="button" className="dep-link" onClick={onOpen}>
          {file.fileName}
        </button>
      </td>
      <td className="small">
        {formatDateTime(file.receivedAt)} · {file.uploadedBy.displayName}
      </td>
      <td className="small">{file.coveredDates.length === 0 ? <span className="muted">aucune</span> : file.coveredDates.map(dayLabel).join(', ')}</td>
      <td>
        <Badge tone={statusTone(file.status)}>{STATUS_LABELS[file.status] ?? file.status}</Badge>
        {file.status === 'published' && file.publishedAt ? <span className="small muted"> depuis {formatDateTime(file.publishedAt)}</span> : null}
      </td>
      <td>
        <button type="button" className="btn btn-sm" onClick={onOpen}>
          {selected ? 'Affiché' : 'Ouvrir'}
        </button>
      </td>
    </tr>
  );
}

// ─── Le fichier en cours ────────────────────────────────────────────────────

function FileDetail(props: {
  detail: DepartureFileDetail;
  hotels: DepartureHotel[];
  canPublish: boolean;
  busy: string | null;
  justPublished: boolean;
  onUpload: (file: File | undefined) => Promise<void>;
  onClose: () => void;
  onAlias: (hotelId: string, label: string) => Promise<void>;
  onCreate: (unknown: DepartureUnknownHotel) => Promise<void>;
  onSynonym: (field: string, header: string) => Promise<void>;
  onPublish: () => Promise<void>;
  onRepublish: () => Promise<void>;
  onUnpublish: () => Promise<void>;
  onDelete: () => Promise<void>;
}): React.ReactElement {
  const { detail, hotels, canPublish, busy, justPublished, onUpload, onClose, onAlias, onCreate, onSynonym, onPublish, onRepublish, onUnpublish, onDelete } = props;
  const { file, report, unknownHotels, rows } = detail;
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [synonymChoice, setSynonymChoice] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<'publish' | 'republish' | 'unpublish' | 'delete' | null>(null);
  // Un fichier qui n'a jamais été vu des accrédités peut être retiré (dépôt par erreur).
  const removable = file.status === 'draft' || file.status === 'rejected';
  const fileInput = useRef<HTMLInputElement | null>(null);

  const eveCount = rows.filter((r) => r.boardingDate !== r.flightDate).length;
  const unknownColumns = report.columns.filter((c) => c.field === null);
  const missingLabels = report.missingColumns.map((f) => FIELD_LABELS[f] ?? f);
  const days = daysPhrase(file.coveredDates);

  // Le titre dit l'état en une phrase ; le bouton dit le prochain geste.
  let tone: Tone = 'info';
  let title = '';
  let text = '';
  if (file.status === 'rejected') {
    tone = 'bad';
    title = "Ce fichier n'a pas pu être lu";
    text =
      missingLabels.length > 0
        ? `Il manque la colonne « ${missingLabels[0]} »${missingLabels.length > 1 ? ` (et ${missingLabels.length - 1} autre${missingLabels.length > 2 ? 's' : ''})` : ''}. Si elle a seulement changé de nom, associez-la ci-dessous, puis déposez le fichier à nouveau.`
        : 'Aucune ligne lisible. Vérifiez le fichier avec l’équipe transport.';
  } else if (file.status === 'published') {
    tone = 'ok';
    title = justPublished ? `Publié : les accrédités voient le plan pour ${days}` : `En ligne : plan pour ${days}`;
    text = `${formatInt(rows.length)} transferts · publié ${file.publishedAt ? formatSince(file.publishedAt) : ''} par ${file.publishedBy?.displayName ?? 'inconnu'}${
      file.pushedAt ? '' : ` · pas encore dans l'application${file.pushError ? ` (${file.pushError})` : ''}`
    }`;
  } else if (file.status === 'superseded') {
    tone = 'neutral';
    title = `Ancien fichier, pour ${days}`;
    text = `Remplacé par un fichier plus récent. Vous pouvez le remettre en ligne tel quel si le nouveau était faux.`;
  } else if (unknownHotels.length > 0) {
    tone = 'warn';
    title = `${formatInt(unknownHotels.length)} nom${unknownHotels.length > 1 ? 's' : ''} d'hôtel à reconnaître avant de publier`;
    text = "L'application a une liste d'hôtels. Pour chaque nom du fichier qu'elle ne connaît pas, dites de quel hôtel il s'agit : elle s'en souviendra pour les fichiers suivants.";
  } else {
    tone = 'ok';
    title = `Prêt à publier : ${formatInt(rows.length)} transferts pour ${days}`;
    text = `${eveCount > 0 ? `${formatInt(eveCount)} départs ont lieu la veille au soir (vols de nuit). ` : ''}Rien n'est visible des accrédités tant que vous n'avez pas publié.`;
  }

  return (
    <div className="stack">
      <section className={`dep-hero is-${tone}`}>
        <div className="dep-hero-body">
          <span className="dep-hero-kicker">
            {file.fileName} · reçu {formatSince(file.receivedAt)} par {file.uploadedBy.displayName}
          </span>
          <h2>{title}</h2>
          <p>{text}</p>
        </div>
        <div className="dep-hero-actions">
          {file.status === 'draft' && unknownHotels.length === 0 ? (
            <button
              type="button"
              className="btn btn-gold"
              disabled={!canPublish || busy !== null || !detail.canPublish || confirming !== null}
              title={canPublish ? undefined : READ_ONLY_NOTICE}
              onClick={() => setConfirming('publish')}
            >
              Publier maintenant
            </button>
          ) : null}
          {file.status === 'superseded' ? (
            <button type="button" className="btn" disabled={!canPublish || busy !== null || confirming !== null} title={canPublish ? undefined : READ_ONLY_NOTICE} onClick={() => setConfirming('republish')}>
              Remettre ce fichier en ligne
            </button>
          ) : null}
          {file.status === 'published' ? (
            <button
              type="button"
              className="btn btn-danger"
              disabled={!canPublish || busy !== null || confirming !== null}
              title={canPublish ? "Les accrédités ne verront plus les transferts de ce fichier. Il redevient un brouillon ici." : READ_ONLY_NOTICE}
              onClick={() => setConfirming('unpublish')}
            >
              Retirer de l'application
            </button>
          ) : null}
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="sr-only"
            disabled={!canPublish || busy !== null}
            onChange={(e) => {
              void onUpload(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <button type="button" className="btn btn-sm" disabled={!canPublish || busy !== null} onClick={() => fileInput.current?.click()}>
            {busy === 'upload' ? 'Lecture…' : 'Déposer un autre fichier'}
          </button>
          {removable ? (
            <button
              type="button"
              className="btn btn-sm btn-danger"
              disabled={!canPublish || busy !== null || confirming !== null}
              title={canPublish ? 'Effacer ce fichier du tableau de bord. Rien ne change pour les accrédités.' : READ_ONLY_NOTICE}
              onClick={() => setConfirming('delete')}
            >
              Retirer ce fichier
            </button>
          ) : null}
          <button type="button" className="btn btn-sm" onClick={onClose}>
            Fermer
          </button>
        </div>
      </section>

      {confirming === 'unpublish' ? (
        <div className="dep-dialog" role="dialog" aria-labelledby="dep-dialog-title">
          <h3 id="dep-dialog-title">Retirer de l'application le plan pour {days} ?</h3>
          <p>
            <strong>Les accrédités ne verront plus ces {formatInt(rows.length)} transferts dans la minute.</strong> Pour ces journées, l'application
            répondra « pas encore disponible », sauf si un autre fichier publié les couvre encore.
          </p>
          <p>
            Ici, le fichier redevient un brouillon : vous pourrez le retirer du tableau de bord ou le republier. Les fichiers qu'il avait remplacés
            restent « anciens ».
          </p>
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
            <button type="button" className="btn" disabled={busy !== null} onClick={() => setConfirming(null)}>
              Annuler
            </button>
            <button type="button" className="btn btn-danger" disabled={busy !== null} onClick={() => void onUnpublish().then(() => setConfirming(null))}>
              {busy === 'unpublish' ? 'Retrait…' : "Oui, retirer de l'application"}
            </button>
          </div>
        </div>
      ) : null}

      {confirming === 'delete' ? (
        <div className="dep-dialog" role="dialog" aria-labelledby="dep-dialog-title">
          <h3 id="dep-dialog-title">Retirer « {file.fileName} » ?</h3>
          <p>
            Le fichier et ce que l'application en a lu sont effacés de ce tableau de bord. <strong>Rien ne change pour les accrédités</strong> :
            ce fichier n'avait pas été publié.
          </p>
          <p>Les hôtels reconnus pendant sa lecture restent connus de l'application.</p>
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
            <button type="button" className="btn" disabled={busy !== null} onClick={() => setConfirming(null)}>
              Annuler
            </button>
            <button type="button" className="btn btn-danger" disabled={busy !== null} onClick={() => void onDelete().then(() => setConfirming(null))}>
              {busy === 'delete' ? 'Retrait…' : 'Oui, retirer'}
            </button>
          </div>
        </div>
      ) : null}

      {confirming === 'publish' || confirming === 'republish' ? (
        <div className="dep-dialog" role="dialog" aria-labelledby="dep-dialog-title">
          <h3 id="dep-dialog-title">{confirming === 'publish' ? `Publier le plan pour ${days} ?` : `Remettre en ligne le plan pour ${days} ?`}</h3>
          <p>
            Les accrédités qui cherchent un vol de ces journées{eveCount > 0 ? ', ou un départ la veille au soir,' : ''} verront ces{' '}
            {formatInt(rows.length)} transferts dans la minute.
          </p>
          <p>
            <strong>Si un plan est déjà en ligne pour l'une de ces journées, il est remplacé pour cette journée-là</strong> et gardé dans les fichiers
            précédents. Les autres journées ne bougent pas.
          </p>
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
            <button type="button" className="btn" disabled={busy !== null} onClick={() => setConfirming(null)}>
              Annuler
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy !== null}
              onClick={() => {
                void (confirming === 'publish' ? onPublish() : onRepublish()).then(() => setConfirming(null));
              }}
            >
              {busy === 'publish' || busy === 'republish' ? 'Publication…' : 'Oui, publier'}
            </button>
          </div>
        </div>
      ) : null}

      {file.status === 'rejected' && unknownColumns.length > 0 ? (
        <Card title="Colonnes que l'application ne reconnaît pas" hint="Si l'une d'elles est en réalité une colonne attendue, dites laquelle : ce nom sera reconnu dans tous les fichiers suivants.">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Dans le fichier</th>
                  <th>C'est en fait…</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {unknownColumns.map((c) => (
                  <tr key={c.header}>
                    <td>{c.header}</td>
                    <td>
                      <select value={synonymChoice[c.header] ?? ''} onChange={(e) => setSynonymChoice((p) => ({ ...p, [c.header]: e.target.value }))}>
                        <option value="">Rien, on l'ignore</option>
                        {Object.entries(FIELD_LABELS).map(([field, label]) => (
                          <option key={field} value={field}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={!canPublish || busy !== null || !synonymChoice[c.header]}
                        onClick={() => void onSynonym(synonymChoice[c.header] ?? '', c.header)}
                      >
                        {busy === `syn:${c.header}` ? '…' : 'Retenir'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      {file.status === 'draft' && unknownHotels.length > 0 ? (
        <Card title="C'est quel hôtel ?" hint="Le nom tel qu'il est écrit dans le fichier, et l'hôtel de l'application qui lui correspond.">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Écrit dans le fichier</th>
                  <th className="num">Lignes</th>
                  <th>Hôtel dans l'application</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {unknownHotels.map((u) => {
                  const chosen = choice[u.key] ?? '';
                  return (
                    <tr key={u.key}>
                      <td>
                        <strong>{u.label}</strong>
                      </td>
                      <td className="num">{formatInt(u.count)}</td>
                      <td>
                        <select value={chosen} onChange={(e) => setChoice((p) => ({ ...p, [u.key]: e.target.value }))} disabled={!canPublish}>
                          <option value="">Choisir dans la liste…</option>
                          {hotels
                            .filter((h) => h.active)
                            .map((h) => (
                              <option key={h.id} value={h.id}>
                                {h.name}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td>
                        <span className="acc-actions">
                          <button type="button" className="btn btn-sm btn-primary" disabled={!canPublish || busy !== null || chosen === ''} onClick={() => void onAlias(chosen, u.label)}>
                            {busy === `alias:${u.label}` ? '…' : "C'est celui-ci"}
                          </button>
                          <button type="button" className="btn btn-sm" disabled={!canPublish || busy !== null} onClick={() => void onCreate(u)}>
                            {busy === `create:${u.key}` ? '…' : "Il n'est pas dans la liste : l'ajouter"}
                          </button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      {file.status === 'rejected' ? null : (
        <details className="fold">
          <summary>
            Voir le détail de la lecture · {formatInt(rows.length)} transferts
            {report.rejectedRows.length > 0
              ? ` · ${formatInt(report.rejectedRows.length)} ligne${report.rejectedRows.length > 1 ? 's' : ''} écartée${report.rejectedRows.length > 1 ? 's' : ''}`
              : ' · toutes les lignes ont été lues'}
          </summary>
          <div className="stack">
            <Card title="Les transferts, tels que l'application les lira" hint="Les colonnes organisation et effectif restent ici, entre opérateurs : elles ne sont jamais envoyées à l'application.">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Embarquement</th>
                      <th>Hôtel</th>
                      <th>Transport</th>
                      <th>Vol</th>
                      <th>Organisation</th>
                    </tr>
                  </thead>
                  {groupByDay(rows).map((day) => (
                    <tbody key={day.date}>
                      <tr className="dep-day">
                        <th colSpan={5} scope="rowgroup">
                          {dayHeading(day.date)} · {formatInt(day.rows.length)} transfert{day.rows.length > 1 ? 's' : ''}
                        </th>
                      </tr>
                      {day.rows.map((r) => {
                        const note = flightDayNote(r.boardingDate, r.flightDate);
                        return (
                          <tr key={r.id}>
                            <td>
                              <span className="mono">{r.boardingTime}</span> · {r.boardingPlace}
                            </td>
                            <td>{r.hotel ? r.hotel.name : <Badge tone="warn">{r.hotelLabel}</Badge>}</td>
                            <td>{modeLabel(r)}</td>
                            <td>
                              <span className="mono">
                                {r.flightNumber} · {r.flightTime}
                              </span>
                              {note ? <span className="small muted"> · {note}</span> : null}
                            </td>
                            <td className="small muted">
                              {r.organization ?? <span className="muted">non renseignée</span>}
                              {r.personCount !== null ? ` · ${formatInt(r.personCount)} pers.` : ''}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  ))}
                </table>
              </div>
              {report.duplicates > 0 ? <p className="small muted" style={{ marginTop: 10 }}>{formatInt(report.duplicates)} ligne(s) en double, comptées une fois.</p> : null}
            </Card>

            {report.rejectedRows.length > 0 ? (
              <Card title="Lignes écartées" hint="Celles que l'application n'a pas pu comprendre (heure ou date illisible). Elles ne sont pas publiées ; le reste du fichier, si.">
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Ligne</th>
                        <th>Pourquoi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rejectedRows.map((r) => (
                        <tr key={`${r.sheet}-${r.rowNumber}`}>
                          <td className="mono">
                            {r.sheet} · {r.rowNumber}
                          </td>
                          <td>{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : null}

            <Card title="Colonnes du fichier" hint="Ce que l'application a reconnu. Une colonne renommée par l'équipe transport apparaît « non reconnue ».">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Dans le fichier</th>
                      <th>Comprise comme</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.columns.map((c) => (
                      <tr key={c.header}>
                        <td>{c.header}</td>
                        <td>
                          {c.field === null ? (
                            <Badge tone="warn">non reconnue</Badge>
                          ) : (
                            <>
                              {FIELD_LABELS[c.field] ?? c.field}
                              {c.sensitive ? <span className="small muted"> · jamais envoyée à l'application</span> : null}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </details>
      )}
    </div>
  );
}

// ─── Hôtels ─────────────────────────────────────────────────────────────────

const STATION_OPTIONS = [
  { value: '', label: 'Gare : non précisée' },
  { value: 'Dakar', label: 'Gare de Dakar' },
  { value: 'Diamniadio', label: 'Gare de Diamniadio' },
];

function HotelsCard(props: {
  hotels: ReturnType<typeof useApi<DepartureHotel[]>>;
  canPublish: boolean;
  busy: string | null;
  onRefresh: () => void;
  run: (key: string, action: () => Promise<void>) => Promise<void>;
}): React.ReactElement {
  const { hotels, canPublish, busy, onRefresh, run } = props;
  const [name, setName] = useState('');
  const [station, setStation] = useState('');
  const [boardingOnly, setBoardingOnly] = useState(false);
  const [zone, setZone] = useState('');
  const [address, setAddress] = useState('');
  const [editing, setEditing] = useState<{ id: string; zone: string; address: string } | null>(null);
  const [notSent, setNotSent] = useState<string | null>(null);

  /** Une fiche modifiée repart vers l'application s'il y a un plan publié ; on dit quand elle n'est pas arrivée. */
  const patch = async (id: string, body: Parameters<typeof api.patchDepartureHotel>[1]): Promise<void> => {
    const out = await api.patchDepartureHotel(id, body);
    setNotSent(out.push && !out.push.ok ? (out.push.error ?? 'service injoignable') : null);
    if (!out.push || out.push.ok) toast('Enregistré');
    onRefresh();
  };

  return (
    <Card hint="C'est la liste que les accrédités voient dans l'application. La gare sert quand le fichier ne la précise pas ; le quartier et l'adresse aident à s'y retrouver (facultatifs).">
      {notSent ? (
        <Banner tone="warn" title="Enregistré ici, mais l'application n'a pas pu être mise à jour">
          {notSent}. Les accrédités voient encore l'ancienne fiche.
        </Banner>
      ) : null}
      {canPublish ? (
        <div className="toolbar" style={{ marginBottom: 14 }}>
          <label className="field">
            Nouvel hôtel
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom affiché aux accrédités" />
          </label>
          <label className="field">
            Gare
            <select value={station} onChange={(e) => setStation(e.target.value)}>
              {STATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Quartier
            <input type="text" value={zone} onChange={(e) => setZone(e.target.value)} placeholder="Almadies" maxLength={80} />
          </label>
          <label className="field">
            Adresse courte
            <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Route de Ngor" maxLength={200} />
          </label>
          <label className="field row" title="Un lieu où l'on monte dans le bus (arrêt CSS, Village), pas un endroit où l'on dort.">
            <input type="checkbox" checked={boardingOnly} onChange={(e) => setBoardingOnly(e.target.checked)} /> point de rendez-vous, pas un hôtel
          </label>
          <button aria-busy={busy === 'hotel:new'}
            type="button"
            className="btn btn-sm btn-primary"
            disabled={busy !== null || name.trim() === ''}
            onClick={() =>
              void run('hotel:new', async () => {
                await api.createDepartureHotel({
                  name: name.trim(),
                  station: station || null,
                  zone: zone.trim() || null,
                  address: address.trim() || null,
                  isBoardingPlaceOnly: boardingOnly,
                });
                setName('');
                setStation('');
                setZone('');
                setAddress('');
                setBoardingOnly(false);
                onRefresh();
              })
            }
          >
            Ajouter
          </button>
        </div>
      ) : null}
      <Async state={hotels}>
        {(items) =>
          items.length === 0 ? (
            <p className="muted">Aucun hôtel pour l'instant : ils se créent au fil des fichiers reçus, ou ci-dessus.</p>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Hôtel</th>
                    <th>Gare</th>
                    <th>Quartier et adresse</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((h) => {
                    const otherSpellings = h.aliases.filter((a) => a.toLowerCase() !== h.name.toLowerCase());
                    return (
                      <tr key={h.id}>
                        <td>
                          <strong>{h.name}</strong>
                          {h.isBoardingPlaceOnly ? (
                            <>
                              {' '}
                              <Badge>point de rendez-vous</Badge>
                            </>
                          ) : null}
                          {h.active ? null : (
                            <>
                              {' '}
                              <Badge tone="bad">retiré</Badge>
                            </>
                          )}
                          {otherSpellings.length > 0 ? (
                            <div className="small muted">aussi écrit : {otherSpellings.join(' · ')}</div>
                          ) : null}
                        </td>
                        <td>
                          {canPublish ? (
                            <select value={h.station ?? ''} disabled={busy !== null} onChange={(e) => void run(`hotel:${h.id}`, () => patch(h.id, { station: (e.target.value || null) as 'Dakar' | 'Diamniadio' | null }))}>
                              {STATION_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            (h.station ?? <span className="muted">non renseignée</span>)
                          )}
                        </td>
                        <td>
                          {editing?.id === h.id ? (
                            <div className="toolbar">
                              <input type="text" aria-label="Quartier" placeholder="Quartier" maxLength={80} value={editing.zone} onChange={(e) => setEditing({ ...editing, zone: e.target.value })} />
                              <input type="text" aria-label="Adresse courte" placeholder="Adresse courte" maxLength={200} value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} />
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                disabled={busy !== null}
                                onClick={() =>
                                  void run(`hotel:${h.id}`, async () => {
                                    await patch(h.id, { zone: editing.zone.trim() || null, address: editing.address.trim() || null });
                                    setEditing(null);
                                  })
                                }
                              >
                                {busy === `hotel:${h.id}` ? '…' : 'Enregistrer'}
                              </button>
                              <button type="button" className="btn btn-sm" disabled={busy !== null} onClick={() => setEditing(null)}>
                                Annuler
                              </button>
                            </div>
                          ) : (
                            <>
                              {h.zone || h.address ? [h.zone, h.address].filter(Boolean).join(' · ') : <span className="muted">non renseigné</span>}{' '}
                              {canPublish ? (
                                <button type="button" className="btn btn-sm" disabled={busy !== null} onClick={() => setEditing({ id: h.id, zone: h.zone ?? '', address: h.address ?? '' })}>
                                  Modifier
                                </button>
                              ) : null}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        }
      </Async>
    </Card>
  );
}
